// src/context/AppContext.jsx
import React, { 
    createContext, 
    useContext, 
    useState, 
    useEffect, 
    useCallback 
} from 'react';
import { 
    onAuthStateChanged, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signOut,
    signInWithPopup
} from 'firebase/auth';
import { 
    collection, 
    doc, 
    onSnapshot,
    query, 
    where, 
    addDoc, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    serverTimestamp,
    getDoc,
    writeBatch
} from 'firebase/firestore';
import { db, auth, provider } from '../utils/firebase';
import { searchProductsWithAI, getProductRecommendations, improveSearchQuery } from '../utils/openaiService';

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isSeller, setIsSeller] = useState(false);
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const [authStep, setAuthStep] = useState('credentials');
    const [authLoading, setAuthLoading] = useState(false);
    const [authForm, setAuthForm] = useState({
        userType: 'buyer',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        company: '',
        businessRegistration: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [recommendedProductIds, setRecommendedProductIds] = useState(new Set());
    
    // AI search related state
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchIntent, setSearchIntent] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [lastSearchQuery, setLastSearchQuery] = useState('');

    // --- Auth Modal Functions ---
    const openAuthModal = useCallback((userType = 'buyer', mode = 'login') => {
        console.log('openAuthModal called with:', { userType, mode });
        setAuthForm(prev => ({ ...prev, userType }));
        setAuthMode(mode);
        setAuthStep('credentials');
        setAuthModalOpen(true);
        console.log('Modal should be open now');
    }, []);

    const closeAuthModal = () => {
        setAuthModalOpen(false);
        setAuthMode('login');
        setAuthStep('credentials');
        setAuthForm({
            userType: 'buyer',
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            phone: '',
            address: '',
            city: '',
            province: '',
            postalCode: '',
            company: '',
            businessRegistration: ''
        });
        setShowPassword(false);
    };

    // --- Cart Functions ---
    const fetchCartData = useCallback((currentId) => {
        if (!currentId) {
            setCart([]);
            return () => {};
        }
        
        console.log('=== Setting up cart listener for user:', currentId);
        const cartQuery = query(
            collection(db, 'cart'), 
            where('userId', '==', currentId)
        );
        
        const unsubscribe = onSnapshot(cartQuery, (snapshot) => {
            const cartData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            console.log('Cart data fetched:', cartData.length, 'items');
            console.log('Cart items:', cartData);
            setCart(cartData);
        }, (error) => {
            console.error("Error fetching cart: ", error);
            console.error("Cart query error details:", {
                code: error.code,
                message: error.message,
                userId: currentId
            });
        });
        
        return () => {
            console.log('=== Cleaning up cart listener ===');
            unsubscribe();
        };
    }, []);

    const updateCartQuantity = useCallback(async (cartItemId, newQuantity) => {
        if (!userId || !auth.currentUser || userId !== auth.currentUser.uid) {
            console.error('❌ Invalid user state for cart update');
            return;
        }
        
        try {
            const cartItemRef = doc(db, 'cart', cartItemId);
            await updateDoc(cartItemRef, { quantity: newQuantity });
        } catch (error) {
            console.error("Error updating cart quantity:", error);
            if (error.code === 'permission-denied') {
                console.error("🔒 Permission denied for cart update - user may not own this item");
            }
        }
    }, [userId]);

    const removeFromCart = useCallback(async (cartItemId) => {
        if (!userId || !auth.currentUser || userId !== auth.currentUser.uid) {
            console.error('❌ Invalid user state for cart removal');
            return;
        }
        
        try {
            const cartItemRef = doc(db, 'cart', cartItemId);
            await deleteDoc(cartItemRef);
        } catch (error) {
            console.error("Error removing from cart:", error);
            if (error.code === 'permission-denied') {
                console.error("🔒 Permission denied for cart removal - user may not own this item");
            }
        }
    }, [userId]);

    const addToCart = useCallback(async (product, quantity) => {
        console.log('=== Add to Cart Start ===');
        console.log('User state:', { user: !!user, userId, authUser: !!auth.currentUser });
        
        if (!user || !userId) {
            console.log('❌ No user or userId, opening auth modal');
            setAuthForm(prev => ({ ...prev, userType: 'buyer' }));
            setAuthMode('login');
            setAuthModalOpen(true);
            return false;
        }

        if (!auth.currentUser) {
            console.log('❌ No authenticated user');
            setAuthForm(prev => ({ ...prev, userType: 'buyer' }));
            setAuthMode('login');
            setAuthModalOpen(true);
            return false;
        }

        // Check user ID match
        if (userId !== auth.currentUser.uid) {
            console.error('❌ User ID mismatch:', { userId, authUid: auth.currentUser.uid });
            throw new Error('User ID mismatch - please refresh and try again');
        }

        try {
            console.log('Product:', product);
            console.log('Quantity:', quantity);
            console.log('User ID:', userId);
            console.log('Auth User ID:', auth.currentUser.uid);
            console.log('Database instance:', db);
            console.log('Auth instance:', auth);
            
            // Check Firebase app info
            console.log('Firebase app name:', db.app.name);
            console.log('Firebase app options:', db.app.options);

            // Check existing cart item
            const existingItem = cart.find(item => item.productId === product.id);
            
            if (existingItem) {
                console.log('Updating existing item:', existingItem.id);
                await updateCartQuantity(existingItem.id, existingItem.quantity + quantity);
            } else {
                console.log('Creating new cart item');
                const cartItem = {
                    productId: product.id,
                    productName: product.name,
                    quantity: quantity,
                    price: (product.moqPrices && product.moqPrices.length > 0) 
                        ? (product.moqPrices[0]?.unitPrice || product.moqPrices[0]?.price || 0)
                        : 0,
                    image: (product.images && product.images.length > 0) ? product.images[0] : '',
                    category: product.category || '',
                    moqPrices: product.moqPrices || [],
                    images: product.images || [],
                    userId: userId,
                    createdAt: serverTimestamp()
                };
                
                console.log('Cart item to add:', cartItem);
                await addDoc(collection(db, 'cart'), cartItem);
                console.log('✅ Cart item added successfully');
            }
            
            return true;
            
        } catch (error) {
            console.error("❌ Add to Cart Failed:", error);
            console.error("Error code:", error.code);
            console.error("Error message:", error.message);
            console.error("Error stack:", error.stack);
            
            // Print more details for permission errors
            if (error.code === 'permission-denied') {
                console.error("🔒 Permission denied error details:");
                console.error("- User authenticated:", !!auth.currentUser);
                console.error("- User ID:", auth.currentUser?.uid);
                console.error("- User ID in context:", userId);
                console.error("- Product ID:", product.id);
                console.error("- Cart item userId:", userId);
                console.error("- Database instance:", db);
                console.error("- Auth instance:", auth);
                console.error("- Firebase app name:", db.app.name);
                console.error("- Firebase project ID:", db.app.options.projectId);
            }
            
            throw error;
        }
    }, [userId, cart, updateCartQuantity]);

    // --- Product Fetching Logic ---
    useEffect(() => {
        console.log('=== Setting up products listener ===');
        const productsQuery = query(
            collection(db, 'products'),
            where('status', '==', 'approved')
        );
        
        const unsubscribe = onSnapshot(productsQuery, (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            console.log('Products fetched:', productsData.length);
            console.log('Product names:', productsData.map(p => p.name));
            console.log('Product details:', productsData);
            setProducts(productsData);
        }, (error) => {
            console.error('Error fetching products:', error);
        });
        
        return () => {
            console.log('=== Cleaning up products listener ===');
            unsubscribe();
        };
    }, []);

    // --- Authentication Logic ---
    useEffect(() => {
        let cartUnsubscribe = () => {};
        
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log('=== Auth State Changed ===');
            console.log('Firebase User:', firebaseUser ? 'Authenticated' : 'Not authenticated');
            console.log('User UID:', firebaseUser?.uid);
            
            // Clean up existing cart listener
            cartUnsubscribe();
            
            if (firebaseUser) {
                const currentId = firebaseUser.uid;
                console.log('Setting userId to:', currentId);
                setUserId(currentId);
                setUser(firebaseUser);
                
                // Get user information
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentId));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        console.log('AppContext - Fetched user data:', userData);
                        console.log('AppContext - User type:', userData.userType);
                        console.log('AppContext - Setting isSeller to:', userData.userType === 'seller');
                        setIsSeller(userData.userType === 'seller');
                        // Add userType to user object (maintain Firebase user info)
                        setUser(prev => {
                            const updatedUser = {
                                ...prev, // Firebase user info (uid, email, displayName, etc.)
                                ...userData, // Firestore user data (userType, firstName, lastName, etc.)
                                uid: prev.uid, // Keep uid from Firebase
                                email: prev.email // Keep email from Firebase
                            };
                            console.log('AppContext - Updated user object:', updatedUser);
                            console.log('AppContext - Final userType:', updatedUser.userType);
                            console.log('AppContext - Final isSeller will be:', updatedUser.userType === 'seller');
                            return updatedUser;
                        });
                    } else {
                        console.log('AppContext - No user document found for:', currentId);
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
                
                // Get cart data
                cartUnsubscribe = fetchCartData(currentId);
            } else {
                setUser(null);
                setUserId(null);
                setIsSeller(false);
                setCart([]);
            }
            setIsAuthReady(true);
        });
        
        return () => {
            cartUnsubscribe();
            unsubscribe();
        };
    }, [fetchCartData]);

    // --- Auth Actions ---
    const signInUser = useCallback(async (form) => {
        setAuthLoading(true);
        try {
            const result = await signInWithEmailAndPassword(auth, form.email, form.password);
            const user = result.user;
            
            // Get user information
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setIsSeller(userData.userType === 'seller');
            }
            
            closeAuthModal();
        } catch (error) {
            console.error('Sign in error:', error);
            alert(`Sign in failed: ${error.message}`);
        } finally {
            setAuthLoading(false);
        }
    }, []);

    const signUpUser = useCallback(async (form) => {
        setAuthLoading(true);
        try {
            const result = await createUserWithEmailAndPassword(auth, form.email, form.password);
            const user = result.user;
            
            const userData = {
                uid: user.uid,
                email: user.email,
                userType: form.userType,
                firstName: form.firstName,
                lastName: form.lastName,
                phone: form.phone,
                address: form.address,
                city: form.city,
                province: form.province,
                postalCode: form.postalCode,
                company: form.company,
                businessRegistration: form.businessRegistration,
                status: 'active',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            
            await setDoc(doc(db, 'users', user.uid), userData);
            closeAuthModal();
        } catch (error) {
            console.error('Sign up error:', error);
            alert(`Sign up failed: ${error.message}`);
        } finally {
            setAuthLoading(false);
        }
    }, []);

    const signOutUser = useCallback(async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Sign out error:', error);
        }
    }, []);

    const signInWithGoogle = useCallback(async (userType = 'buyer') => {
        setAuthLoading(true);
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (!userDoc.exists()) {
                const userData = {
                    uid: user.uid,
                    email: user.email,
                    userType: userType, // Use passed userType
                    firstName: user.displayName?.split(' ')[0] || '',
                    lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
                    status: 'active',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };
                await setDoc(doc(db, 'users', user.uid), userData);
            }
            closeAuthModal();
        } catch (error) {
            console.error('Google sign in error:', error);
            alert(`Google sign in failed: ${error.message}`);
        } finally {
            setAuthLoading(false);
        }
    }, []);

    // --- Other Functions ---
    const checkout = useCallback(async () => {
        if (cart.length === 0) return;
        
        setLoading(true);
        try {
            const batch = writeBatch(db);
            const orderRef = doc(collection(db, 'orders'));
            
            const orderData = {
                userId: userId,
                items: cart.map(item => ({
                    productId: item.productId,
                    productName: item.productName,
                    quantity: item.quantity,
                    price: item.price
                })),
                total: cart.reduce((sum, item) => sum + (item.price * item.quantity), 0),
                status: 'pending',
                createdAt: serverTimestamp()
            };
            
            batch.set(orderRef, orderData);
            
            // Delete cart items
            cart.forEach(item => {
                batch.delete(doc(db, 'cart', item.id));
            });
            
            await batch.commit();
            setCart([]);
        } catch (error) {
            console.error('Checkout error:', error);
        } finally {
            setLoading(false);
        }
    }, [cart, userId]);

    const uploadProduct = useCallback(async (productData) => {
        if (!userId) return null;
        
        setLoading(true);
        try {
            const product = {
                ...productData,
                sellerId: userId,
                status: 'pending',
                viewCount: 0,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            
            const docRef = await addDoc(collection(db, 'products'), product);
            return docRef.id;
        } catch (error) {
            console.error('Upload error:', error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // --- AI search functions ---
    const performAISearch = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            setSearchIntent('');
            setSearchSuggestions([]);
            setLastSearchQuery('');
            return;
        }

        setSearchLoading(true);
        setLastSearchQuery(query);
        
        try {
            const aiResult = await searchProductsWithAI(query, products);
            
            // Match AI results with actual product data
            const matchedProducts = aiResult.matchedProducts
                .map(match => {
                    const product = products.find(p => p.id === match.id);
                    return product ? { ...product, relevanceScore: match.relevanceScore, matchReason: match.reason } : null;
                })
                .filter(Boolean)
                .sort((a, b) => b.relevanceScore - a.relevanceScore);

            setSearchResults(matchedProducts);
            setSearchIntent(aiResult.searchIntent);
            setSearchSuggestions(aiResult.suggestions);
        } catch (error) {
            console.error('AI search error:', error);
            // Fallback search
            const fallbackResults = products.filter(product => 
                product.name.toLowerCase().includes(query.toLowerCase()) ||
                product.description?.toLowerCase().includes(query.toLowerCase()) ||
                product.category?.toLowerCase().includes(query.toLowerCase())
            );
            setSearchResults(fallbackResults);
            setSearchIntent('Keyword search');
            setSearchSuggestions([]);
        } finally {
            setSearchLoading(false);
        }
    }, [products]);

    const getProductRecommendationsAI = useCallback(async (currentProduct) => {
        try {
            const aiResult = await getProductRecommendations(currentProduct, products);
            
            // Match AI recommendation results with actual product data
            const recommendedProducts = aiResult.recommendations
                .map(rec => {
                    const product = products.find(p => p.id === rec.id);
                    return product ? { 
                        ...product, 
                        relevanceScore: rec.relevanceScore, 
                        recommendationReason: rec.reason,
                        recommendationType: rec.type
                    } : null;
                })
                .filter(Boolean)
                .sort((a, b) => b.relevanceScore - a.relevanceScore);

            return {
                products: recommendedProducts,
                strategy: aiResult.recommendationStrategy
            };
        } catch (error) {
            console.error('AI recommendation error:', error);
            // Fallback recommendations
            const fallbackProducts = products
                .filter(p => p.id !== currentProduct.id && p.category === currentProduct.category)
                .slice(0, 6);
            return {
                products: fallbackProducts,
                strategy: 'Category-based recommendations'
            };
        }
    }, [products]);

    const improveSearchQueryAI = useCallback(async (query) => {
        try {
            const improved = await improveSearchQuery(query);
            return improved;
        } catch (error) {
            console.error('Query improvement error:', error);
            return {
                improvedQuery: query,
                keywords: [query],
                searchType: 'broad'
            };
        }
    }, []);

    const value = {
        user, userId, isSeller, products, cart, loading, isAuthReady,
        authModalOpen, authMode, authStep, authLoading, authForm, showPassword,
        recommendedProductIds, setRecommendedProductIds,
        searchResults, searchLoading, searchIntent, searchSuggestions, lastSearchQuery,
        setAuthModalOpen, setAuthMode, setAuthStep, setAuthForm, setShowPassword,
        signInUser, signUpUser, signOutUser, signInWithGoogle, closeAuthModal, openAuthModal,
        addToCart, updateCartQuantity, removeFromCart, checkout, uploadProduct,
        performAISearch, getProductRecommendationsAI, improveSearchQueryAI,
        setProducts, setLoading
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};